
import React, { useEffect, useMemo, useRef } from "react";
import { formatDeviceCode, syncCodeWithPairings } from "../utils/deviceCodeUtils";

interface PlayerActivationScreenProps {
  deviceCode: string;
  deviceId?: string;
  onGenerateNewCode?: () => void;
  onActivated?: (driverId: string) => void;
}

const PlayerActivationScreen: React.FC<PlayerActivationScreenProps> = ({
  deviceCode,
  deviceId,
  onGenerateNewCode,
  onActivated
}) => {
  const hasSyncedRef = useRef(false);

  const codeToShow = useMemo(() => {
    if (deviceCode && deviceCode.trim()) return deviceCode;
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = urlParams.get('code');
    if (urlCode && urlCode.trim()) return formatDeviceCode(urlCode);
    const sessionCode = sessionStorage.getItem('player_device_code');
    if (sessionCode && sessionCode.trim()) return sessionCode;
    const localCode = localStorage.getItem('deviceCode');
    if (localCode && localCode.trim()) return formatDeviceCode(localCode);
    return '';
  }, [deviceCode]);

  // Sincronizar código com tabela device_pairings para integração com Companion
  // Isso é feito APENAS UMA VEZ quando o código é exibido
  useEffect(() => {
    if (codeToShow && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      console.log("[PlayerActivationScreen] Sincronizando código com device_pairings:", codeToShow);
      syncCodeWithPairings(codeToShow).then((success) => {
        if (success) {
          console.log("[PlayerActivationScreen] ✅ Código sincronizado! Companion pode ativar.");
        } else {
          console.warn("[PlayerActivationScreen] ⚠️ Falha ao sincronizar código");
        }
      });
    }
  }, [codeToShow]);

  // NOTA: A verificação de ativação pelo Companion é feita pelo useDeviceActivation
  // Este componente apenas exibe o código e sincroniza com a tabela device_pairings
  // O useDeviceActivation verifica a tabela device_pairings periodicamente

  useEffect(() => {
    console.log("Código exibido no PlayerActivationScreen:", codeToShow);
  }, [codeToShow]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A3622] to-[#071E14] flex flex-col items-center justify-center p-8">
      <div className="text-center space-y-4 max-w-3xl w-full"
        style={{
          backgroundImage:
            "radial-gradient(circle at 10% 20%, rgba(20, 80, 60, 0.15) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(10, 150, 100, 0.1) 0%, transparent 60%)",
        }}
      >
        {/* Logo com efeito de brilho */}
        <div className="-mb-16 relative">
          <div className="absolute inset-0 bg-[#39b92c] opacity-20 blur-xl rounded-full"></div>
          <div className="w-52 h-52 flex items-center justify-center mx-auto relative">
            <img
              src="https://locaninja.com/logotipo-watch-white.png"
              alt="Watch Logo"
              className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
            />
          </div>
        </div>

        {/* Título Principal */}
        <div className="space-y-0 -mt-28">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-1">
            Ative este aparelho
          </h1>
          <p className="text-lg text-[#68C095] mt-2">
            Use este código no painel ou companion para ativar o aparelho
          </p>
        </div>

        {/* Código de Ativação */}
        <div className="bg-[#0D4C31]/80 backdrop-blur-md rounded-2xl p-8 border border-[#39b92c]/30 shadow-lg mx-auto max-w-xl transition-all duration-300 hover:shadow-[0_0_30px_rgba(57,185,44,0.2)]">
          {/* Indicador de aguardando */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-[#39b92c] rounded-full animate-pulse"></div>
            <span className="text-sm text-[#68C095]">Aguardando ativação...</span>
          </div>

          <h2 className="text-2xl text-[#A3E0C6] mb-4 font-semibold">Código de Ativação</h2>
          <div className="text-4xl md:text-5xl font-mono font-bold text-white tracking-normal py-4 transition-all">
            {codeToShow}
          </div>
          <p className="text-[#A3E0C6] mt-4">
            Após a ativação, as mídias serão<br />exibidas automaticamente neste dispositivo.
          </p>
        </div>

        {/* ID do Dispositivo */}
        <div className="text-sm text-[#68C095] mt-4 flex flex-col items-center justify-center">
          <p className="font-medium">ID do aparelho:</p>
          {deviceId && (
            <p className="text-xs text-[#68C095]/70 mt-1 font-mono">
              {deviceId}
            </p>
          )}
        </div>

        <div className="opacity-0 hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={onGenerateNewCode}
            className="text-xs text-[#68C095] hover:text-[#A3E0C6] transition-colors"
          >
            Gerar novo código
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerActivationScreen;
